/**
 * 用于显示简短的自动消失通知
 */
class AlertObject {
    constructor() {
        this.alertsQueue = {};
        this.defaultOptions = {
            position: 'top-right',
            duration: 3000,
            type: 'info',
            icon: '',
            maxAlerts: 5,
            showProgress: true,
            title: '', // 新增：可选标题
            showClose: true, // 新增：是否显示关闭按钮
            html: false // 新增：内容是否为HTML
        };
        this.init();
    }

    /**
     * 初始化 Alert 组件
     */
    init() {
        // 初始化各位置的队列
        const positions = ['top-right', 'bottom-right', 'top-left', 'bottom-left', 'top-center', 'bottom-center'];
        positions.forEach(position => {
            this.alertsQueue[position] = [];
        });
    }

    /**
     * 显示 Alert 通知
     * @param {string} message - 消息内容
     * @param {Object} options - 选项配置
     */
    show(message, options = {}) {
        const config = { ...this.defaultOptions, ...options };
        const { position, duration, type, icon, maxAlerts, showProgress, title, showClose, html } = config;
        if (!this.alertsQueue[position]) {
            this.alertsQueue[position] = [];
        }
        if (this.alertsQueue[position].length >= maxAlerts) {
            const oldestAlert = this.alertsQueue[position].shift();
            this.hideAlert(oldestAlert, true);
        }
        const tipElement = this.createAlertElement(message, type, icon, duration, showProgress, title, showClose, html);
        let closeTimer = null;
        let startTime = Date.now();
        let remaining = duration;
        // 进度光效条动画控制
        const progressBar = tipElement.querySelector('.alert-progress');
        if (progressBar) {
            progressBar.classList.add('alert-progress-animate');
            progressBar.style.animationDuration = duration + 'ms';
        }
        // 定时关闭
        const startCloseTimer = () => {
            closeTimer = setTimeout(() => {
                this.hideAlert(tipElement);
            }, remaining);
        };
        const clearCloseTimer = () => {
            if (closeTimer) {
                clearTimeout(closeTimer);
                closeTimer = null;
            }
        };
        startCloseTimer();
        // 鼠标悬停反向动画并暂停关闭
        tipElement.addEventListener('mouseenter', () => {
            clearCloseTimer();
            // 计算剩余时间
            if (progressBar) {
                progressBar.classList.remove('alert-progress-animate');
                progressBar.classList.add('alert-progress-reverse');
                progressBar.style.animationDuration = '400ms';
            }
        });
        tipElement.addEventListener('mouseleave', () => {
            startTime = Date.now();
            startCloseTimer();
            if (progressBar) {
                progressBar.classList.remove('alert-progress-reverse');
                progressBar.classList.add('alert-progress-animate');
                progressBar.style.animationDuration = remaining + 'ms';
            }
        });
        // 使用统一消息容器管理器添加元素
        if (window.messageContainerManager?.addMessage(tipElement, position)) {
            this.alertsQueue[position].push(tipElement);
            // 在下一帧后再添加类，确保浏览器已应用初始 transform，从而触发滑入动画
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    tipElement.classList.add('show');
                    tipElement.classList.add('alert-show');
                });
            });
        } else {
            // 如果无法添加到统一容器，回退到直接添加到 body
            if (document.body) {
                document.body.appendChild(tipElement);
                this.alertsQueue[position].push(tipElement);
                tipElement.style.position = 'fixed';
                // 使用全局管理器的zIndex或默认值
                const zIndex = window.messageContainerManager?.getNextZIndex() || 1000;
                tipElement.style.zIndex = zIndex;
                this.setAlertPosition(tipElement, position);
                // 在下一帧后再添加类，确保初始样式生效
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        tipElement.classList.add('show');
                        tipElement.classList.add('alert-show');
                    });
                });
            }
        }
        // 关闭时清理 timer
        tipElement._clearCloseTimer = () => clearTimeout(closeTimer);
        return tipElement;
    }

    /**
     * 设置 Alert 元素的位置（回退方案）
     */
    setAlertPosition(element, position) {
        const positions = {
            'top-right': { top: '20px', right: '20px' },
            'top-left': { top: '20px', left: '20px' },
            'bottom-right': { bottom: '20px', right: '20px' },
            'bottom-left': { bottom: '20px', left: '20px' },
            'top-center': { top: '20px', left: '50%', transform: 'translateX(-50%)' },
            'bottom-center': { bottom: '20px', left: '50%', transform: 'translateX(-50%)' }
        };

        const pos = positions[position] || positions['top-right'];
        Object.assign(element.style, pos);
    }

    /**
     * 创建 Alert 元素
     */
    createAlertElement(message, type, customIcon, duration, showProgress, title, showClose, html) {
        const tipElement = document.createElement('div');
        tipElement.className = `alert alert-${type} fade`;
        tipElement.setAttribute('role', 'alert');

        // 图标
        const iconMap = {
            success: '✓',
            error: '✕',
            warning: '⚠',
            info: 'i'
        };
        const iconText = customIcon || iconMap[type] || 'i';

        // 关闭按钮
        let closeBtn = '';
        if (showClose) {
            closeBtn = `<button type="button" class="alert-close" aria-label="Close">&times;</button>`;
        }

        // 标题
        let heading = '';
        if (title) {
            heading = `<div class="alert-heading">${title}</div>`;
        }

        // 内容
        let content = '';
        if (html) {
            content = `<div class="alert-content">${message}</div>`;
        } else {
            content = `<div class="alert-content">${this.escapeHtml(message)}</div>`;
        }

        // 图标
        const iconHtml = `<div class="alert-icon">${iconText}</div>`;

        // 进度光效条
        let progressBar = '';
        if (showProgress) {
            progressBar = `<div class="alert-progress"></div>`;
        }
        tipElement.innerHTML = `${closeBtn}${iconHtml}<div style="flex:1;min-width:0;">${heading}${content}</div>${progressBar}`;

        // 关闭事件
        if (showClose) {
            tipElement.querySelector('.alert-close').onclick = (e) => {
                e.stopPropagation();
                this.hideAlert(tipElement);
            };
        }

        return tipElement;
    }

    /**
     * 隐藏 Alert
     */
    hideAlert(tipElement, immediate = false) {
        if (!tipElement || !tipElement.parentNode) return;
        if (tipElement._clearCloseTimer) tipElement._clearCloseTimer();
        tipElement.classList.remove('show');
        // 移除 alert-show，以便回退到隐藏的平移动画状态
        tipElement.classList.remove('alert-show');
        tipElement.classList.add('alert-hide');

        // 从队列中移除
        Object.keys(this.alertsQueue).forEach(position => {
            const index = this.alertsQueue[position].indexOf(tipElement);
            if (index > -1) {
                this.alertsQueue[position].splice(index, 1);
            }
        });

        // 延迟移除DOM元素（与CSS动画时长保持一致）
        setTimeout(() => {
            // 使用全局消息容器管理器移除和清理
            window.messageContainerManager?.removeMessage(tipElement);
            window.messageContainerManager?.cleanup();
        }, immediate ? 0 : 400);
    }

    /**
     * 显示成功 Alert
     */
    success(message, options = {}) {
        console.log('Alert success:', message);
        return this.show(message, { ...options, type: 'success' });
    }

    /**
     * 显示错误 Alert
     */
    error(message, options = {}) {
        console.error('Alert error:', message);
        return this.show(message, { ...options, type: 'error' });
    }

    /**
     * 显示警告 Alert
     */
    warning(message, options = {}) {
        console.warn('Alert warning:', message);
        return this.show(message, { ...options, type: 'warning' });
    }

    /**
     * 显示信息 Alert
     */
    info(message, options = {}) {
        console.info('Alert info:', message);
        return this.show(message, { ...options, type: 'info' });
    }

    /**
     * 清除指定位置的所有 Alerts
     */
    clear(position = null) {
        if (position) {
            if (this.alertsQueue[position]) {
                this.alertsQueue[position].forEach(tip => this.hideAlert(tip, true));
                this.alertsQueue[position] = [];
            }
        } else {
            // 清除所有位置的 Alerts
            Object.keys(this.alertsQueue).forEach(pos => {
                this.alertsQueue[pos].forEach(tip => this.hideAlert(tip, true));
                this.alertsQueue[pos] = [];
            });
        }
        // 清理空容器
        setTimeout(() => window.messageContainerManager?.cleanup(), 50);
    }

    /**
     * 销毁 Alert 组件
     */
    destroy() {
        // 清除所有 Alerts
        this.clear();

        // 重置队列
        this.alertsQueue = {};
    }

    // 新增：HTML转义
    escapeHtml(str) {
        return String(str).replace(/[&<>"']/g, function (s) {
            return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[s]);
        });
    }
}


// 新增：Alert 静态类封装
class Alert {
    static _instance = new AlertObject();
    static show(message, options) {
        return Alert._instance.show(message, options);
    }
    static success(message, options) {
        return Alert._instance.success(message, options);
    }
    static error(message, options) {
        return Alert._instance.error(message, options);
    }
    static warning(message, options) {
        return Alert._instance.warning(message, options);
    }
    static info(message, options) {
        return Alert._instance.info(message, options);
    }
    static clear(position) {
        return Alert._instance.clear(position);
    }
    static destroy() {
        return Alert._instance.destroy();
    }
}


    // 立即将 Alert 暴露到全局（确保在 script 标签中立即可用）
    // 这是一个 best-effort 设置：如果脚本已被打包为 IIFE（如 esbuild 的 iife 输出），
    // 仍然会在此尝试将类赋值到全局命名空间，避免内联 script 因加载顺序而抛出 ReferenceError。
    try {
        if (typeof window !== 'undefined') {
            window.Alert = window.Alert || Alert;
            globalThis.Alert = globalThis.Alert || Alert;
            // 兼容性别名
            window.alertInstance = window.alertInstance || Alert;
        }
    } catch (e) {
        // 在非常受限的环境中可能会失败，静默降级
    }

    function initializeComponents() {
        // 初始化期间再次确保全局引用正确（安全覆盖）
        window.Alert = Alert;
        globalThis.Alert = Alert;
        window.alertInstance = Alert;
        console.log('✅ Alert component initialized');
    }

// 等待 ui.js 加载完成（确保 messageContainerManager 可用）
function waitForDependencies(callback, maxAttempts = 50) {
    let attempts = 0;
    
    function check() {
        attempts++;
        
        // 检查是否已经有 messageContainerManager（ui.js 已加载）
        // 或者达到最大尝试次数（降级处理，即使没有也继续）
        if (window.messageContainerManager || attempts >= maxAttempts) {
            if (!window.messageContainerManager) {
                console.warn('⚠️ Alert: messageContainerManager not found, using fallback mode');
            }
            callback();
        } else {
            // 每 20ms 检查一次
            setTimeout(check, 20);
        }
    }
    
    check();
}

// 在 DOM 准备就绪且依赖加载完成后初始化
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        waitForDependencies(initializeComponents);
    });
} else {
    // DOM 已经准备就绪，等待依赖
    waitForDependencies(initializeComponents);
}

// 新增：自动为页面中已有的 inline .alert 元素绑定关闭和显示行为
function initInlineAlerts() {
    if (!document.body) return;
    const alerts = document.querySelectorAll('.alert');
    alerts.forEach(alertEl => {
        // 如果已经初始化，跳过
        if (alertEl.hasAttribute('data-alert-initialized')) return;
        alertEl.setAttribute('data-alert-initialized', '1');

        // 如果有 .alert-close 按钮，绑定关闭行为
        const closeBtn = alertEl.querySelector('.alert-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                // 添加隐藏类，随后移除元素
                alertEl.classList.remove('alert-show');
                alertEl.classList.add('alert-hide');
                setTimeout(() => {
                    if (alertEl.parentNode) alertEl.parentNode.removeChild(alertEl);
                }, 400);
            });
        }

        // 如果元素带有 data-autoshow="false" 则不自动添加 show 类
        const autoshow = alertEl.getAttribute('data-autoshow');
        if (autoshow === 'false') return;

        // 确保有 fade 基类以便动画起作用
        if (!alertEl.classList.contains('fade')) alertEl.classList.add('fade');

        // 延迟加上 show 类以触发 CSS 动画
        setTimeout(() => {
            alertEl.classList.add('show');
            alertEl.classList.add('alert-show');
        }, 10);
    });
}

// 在 DOMContentLoaded 时初始化 inline alerts（兼容直接包含在页面的 .alert）
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInlineAlerts);
} else {
    initInlineAlerts();
}